import React, { useState } from 'react'
import PropTypes from 'prop-types'

import { StyledInnerMobileWrapper, StyledOuterMobileWrapper } from './styles'
import { ResponsiveContainer } from '../../atoms/responsiveContainer'
import { CarouselHeader } from '../../atoms/carouselHeader'
import { CarouselWrapper } from '../../atoms/carouselWrapper'
import { ContentUnWrapper } from '../../atoms/contentWrapper'
import { LogoLink } from '../../atoms/logoLink'
import { themeSizes } from '../../../styles/theme'
import { getActiveItems } from '../../../dataManipulation/cmsItems'
import { LogosCarouselFallback } from './fallback'
import {
  normalizeDataLayerText,
  pushDataLayer,
} from '../../../utils/pushDataLayer'
import { getPathNavigation } from '../../../utils/getWebsiteUrl'

const LogosCarousel = (props) => {
  const { content, triggerGAEvent, screenName } = props

  const parseContent = () => {
    if (!content.logos_list?.length && !content.providers?.length) return []

    if (content.acf_fc_layout == 'logos_area') {
      return getActiveItems(content?.logos_list ?? [])
    }

    return content.providers
      ?.filter(
        (provider) =>
          provider.active &&
          provider.vertical_lobby_display.includes(
            content.casinoType.replaceAll('-', '_')
          )
      )
      ?.map((provider) => {
        return {
          logo_text: provider.name,
          logo_image: provider.logo,
          logo_link: `${content.link}${provider.slug}`,
        }
      })
  }

  const cards = parseContent()

  const { length } = cards
  const maxItemsVisible = 6
  const maxIndex = Math.ceil(length / maxItemsVisible)

  const [index, setIndex] = useState(0)

  if (!content?.logos_list?.length && !content?.providers?.length)
    return <LogosCarouselFallback />

  if (length === 0) return <></>

  const getLink = (card) => {
    const path = card.logo_link.replaceAll('&amp;', '&')
    return getPathNavigation(path)
  }

  const onNext = () => {
    const newIndex = index + 1
    if (newIndex < maxIndex) setIndex(newIndex)
    else setIndex(0)
  }

  const onPrevious = () => {
    const newIndex = index - 1
    if (newIndex >= 0) setIndex(newIndex)
    else setIndex(maxIndex - 1)
  }

  const onLogoClick = ({ position, logo_text, logo_link }) => {
    if (triggerGAEvent) {
      pushDataLayer({
        eventName: 'provider-opened',
        data: {
          location: screenName,
          position,
          provider_name: normalizeDataLayerText(logo_text),
          provider_id: logo_link,
        },
      })
    }
  }

  const createCards = () => {
    return cards.map((card, i) => (
      <LogoLink
        key={`pm-child-${i}`}
        logo={card.logo_image}
        link={getLink(card)}
        label={card.logo_text}
        onClick={() => onLogoClick({ ...card, position: i })}
      />
    ))
  }

  const createMobileVersion = (key) => (
    <ContentUnWrapper addPaddingToChild>
      <StyledOuterMobileWrapper key={key}>
        <StyledInnerMobileWrapper length={length}>
          {createCards()}
        </StyledInnerMobileWrapper>
      </StyledOuterMobileWrapper>
    </ContentUnWrapper>
  )

  return (
    <div>
      <CarouselHeader title={content.header_title} href={content.link} />
      <ResponsiveContainer
        sizeNumber={themeSizes.desktop}
        render={() => createMobileVersion('MobileLogosCarousel')}
        renderElse={() => (
          <CarouselWrapper
            key="DesktopLogosCarousel"
            length={length}
            index={index}
            onNext={onNext}
            onPrevious={onPrevious}
            maxItemsVisible={maxItemsVisible}
          >
            {createCards()}
          </CarouselWrapper>
        )}
        renderFallback={() => createMobileVersion('FallbackLogosCarousel')}
      />
    </div>
  )
}

const leaguesPropType = PropTypes.shape({
  acf_fc_layout: PropTypes.string,
  header_title: PropTypes.string,
  link: PropTypes.string,
  logos_list: PropTypes.arrayOf(
    PropTypes.shape({
      activeFrom: PropTypes.number,
      activeTo: PropTypes.number,
      logo_link: PropTypes.string,
      logo_text: PropTypes.string,
      logo_image: PropTypes.shape({
        url: PropTypes.string,
        alt: PropTypes.string,
      }),
    })
  ),
})

const providersPropType = PropTypes.shape({
  acf_fc_layout: PropTypes.string,
  header_title: PropTypes.string,
  link: PropTypes.string,
  casinoType: PropTypes.string,
  providers: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      id: PropTypes.string,
      slug: PropTypes.string,
      active: PropTypes.bool,
      vertical_lobby_display: PropTypes.arrayOf(PropTypes.string),
      logo: PropTypes.shape({
        url: PropTypes.string,
        alt: PropTypes.string,
      }),
    })
  ),
})

LogosCarousel.propTypes = {
  content: PropTypes.oneOfType([leaguesPropType, providersPropType]),
  triggerGAEvent: PropTypes.bool,
  screenName: PropTypes.string,
}

export { LogosCarousel }
